import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent } from 'react';
import { useForm } from 'react-hook-form';

import Button from '@/components/Button';
import { FormLayout } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import useAlerts from '@/hooks/useAlerts';
import useOnCloseWithConfirmation from '@/hooks/useOnCloseWithConfirmation';
import { EmployeeResource, ReleaseResource } from '@/types/resources';
import { api } from '@/utils/api';
import { productionTeams } from '@/utils/data';
import { compilationGroups } from '@/utils/data/distribution';
import {
  handleFormErrors,
  stringsArrayToDropdownOptions,
} from '@/utils/helpers';

import { compilationTypes } from './DistributionScheduleModal';

const compilationGroupOptions =
  stringsArrayToDropdownOptions(compilationGroups);

interface FormDataT {
  notes: string;
  compilation_type?: number;
  compilation_group?: string;
  part?: number;
  production_team?: string;
  fresh_id?: string;
  director?: number;
  editor?: number;
  publisher?: number;
  archivist?: number;
}

interface Props {
  onClose: () => void;
  editableRelease: ReleaseResource;
}

const EditReleaseInformationForm: FC<Props> = ({
  onClose,
  editableRelease,
}) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const { data: employees } = useQuery(
    ['employees'],
    () => api.get<never, EmployeeResource[]>('/v1/employees'),
    {
      staleTime: 120 * 1000,
      select: (data) => {
        const mapFn = (opt: EmployeeResource) => ({
          label: opt.name,
          value: opt.id,
        });

        const filterFn = (opt: EmployeeResource, roleTitle: string) =>
          opt.roles!.map((r) => r.title).includes(roleTitle);

        return {
          directors: data.filter((e) => filterFn(e, 'Director')).map(mapFn),
          editors: data.filter((e) => filterFn(e, 'Editor')).map(mapFn),
          publishers: data.filter((e) => filterFn(e, 'Publisher')).map(mapFn),
          archivists: data.filter((e) => filterFn(e, 'Archivist')).map(mapFn),
        };
      },
    },
  );

  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    formState: { isSubmitting, isDirty },
  } = useForm<FormDataT>({
    defaultValues: {
      notes: editableRelease?.notes ?? '',
      compilation_type: editableRelease?.compilationType,
      compilation_group: editableRelease?.compilationGroup,
      part: editableRelease?.part,
      production_team: editableRelease?.productionTeam,
      fresh_id: editableRelease?.freshId ?? '',
      director: editableRelease?.employees?.find(
        (e) => e.release_role === 'director',
      )?.id,
      editor: editableRelease?.employees?.find(
        (e) => e.release_role === 'editor',
      )?.id,
      publisher: editableRelease?.employees?.find(
        (e) => e.release_role === 'publisher',
      )?.id,
      archivist: editableRelease?.employees?.find(
        (e) => e.release_role === 'archivist',
      )?.id,
    },
  });

  const closeDialog = useOnCloseWithConfirmation(onClose, isDirty);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    return handleSubmit(async (data) => {
      try {
        await api.put(`/v1/releases/${editableRelease.id}/information`, data);

        queryClient.invalidateQueries(['releases'], {
          exact: false,
        });

        alerts.success(
          `Release has been successfully updated`,
          editableRelease.title,
        );
        onClose();
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts);
      }
    })(e);
  };

  return (
    <Modal>
      <SidePanel heading={'Edit release information'} onClose={closeDialog}>
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <FormField<FormDataT, string>
                control={control}
                options={productionTeams.map((t) => ({
                  label: t,
                  value: t,
                }))}
                name="production_team"
                label="Production Team"
                type="select"
              />
              <FormField<FormDataT>
                control={control}
                name="fresh_id"
                placeholder="Add ID"
                label="Fresh ID (optional)"
              />
              <FormField<FormDataT>
                control={control}
                name="notes"
                label="Note"
                subLabel="(Internal Information)"
                type="textarea"
              />
              <FormField<FormDataT, number>
                control={control}
                type="select"
                name="director"
                label="Director"
                options={employees?.directors ?? []}
              />
              <FormField<FormDataT, number>
                control={control}
                type="select"
                name="editor"
                label="Editor"
                options={employees?.editors ?? []}
              />
              <FormField<FormDataT, number>
                control={control}
                type="select"
                name="publisher"
                label="Publisher"
                options={employees?.publishers ?? []}
              />
              <FormField<FormDataT, number>
                control={control}
                type="select"
                name="archivist"
                label="Archivist"
                options={employees?.archivists ?? []}
              />
              <FormField<FormDataT, number>
                control={control}
                type="select"
                name="part"
                label="Part"
                options={Array.from(Array(10).keys()).map((part) => ({
                  value: part,
                  label: part.toString(),
                }))}
                menuPlacement="top"
              />
              <FormField<FormDataT, string>
                control={control}
                type="select"
                name="compilation_group"
                label="Compilation Group"
                options={compilationGroupOptions}
                menuPlacement="top"
              />
              <FormField<FormDataT, number>
                control={control}
                type="select"
                name="compilation_type"
                label="Compilation type"
                options={compilationTypes}
                menuPlacement="top"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              type="submit"
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              Save
            </Button>
            <Button color="secondary" type="button" onClick={closeDialog}>
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default EditReleaseInformationForm;
